import type { CamposDeMaquinaria, MaquinariaDeInforme, Moneda } from "@/lib/informes";
import { textoDeDecimal, textoDeNumero } from "@/lib/montos";
import { aDosDecimales, numeroEscrito } from "@/lib/numeroEscrito";

/* ---------------------------------------------------------------------------
   La pestaña "Maquinarias y equipos": traducción formulario ↔ API.

   VIVE APARTE DEL COMPONENTE porque acá está el riesgo real de la pantalla: el
   punto de miles. Un "45.000" leído como decimal deja la fila perfectamente
   formada valiendo 45, en un informe pericial. Eso se prueba, no se mira.

   EL FORMATO DE PRESENTACIÓN SE FUE A `lib/montos.ts` cuando entraron las
   pestañas de instalaciones y construcciones: eran setenta líneas que no
   sabían nada de maquinaria y las tres las necesitan iguales.
--------------------------------------------------------------------------- */

/**
 * Una máquina mientras se escribe.
 *
 * TODO ES STRING porque eso es lo que hay en un `<input>`: guardarlo como
 * número obligaría a decidir qué es un campo a medio teclear ("-", "1,") y la
 * respuesta siempre sería borrar lo que la persona está escribiendo.
 */
export interface FormularioDeMaquina {
  nombre: string;
  anio_fabricacion: string;
  unidades: string;
  vida_util_total: string;
  vida_util_residual: string;
  valor_unitario_reposicion: string;
  valor_unitario_residual: string;
  ajuste_comercial_pct: string;
  moneda: Moneda;
}

/**
 * El formulario recién abierto.
 *
 * `unidades` arranca en "1" y `moneda` en USD: son los dos únicos campos con
 * valor inicial, y los dos porque el request los exige. Pedirle a quien carga
 * que teclee "1" en cada máquina sería ruido; dejarlos vacíos sería un 422
 * sobre algo que nadie decidió.
 */
export const MAQUINA_VACIA: FormularioDeMaquina = {
  nombre: "",
  anio_fabricacion: "",
  unidades: "1",
  vida_util_total: "",
  vida_util_residual: "",
  valor_unitario_reposicion: "",
  valor_unitario_residual: "",
  ajuste_comercial_pct: "",
  moneda: "USD",
};

/** Carga el formulario con una máquina ya guardada, para corregirla. */
export function formularioDesde(maquina: MaquinariaDeInforme): FormularioDeMaquina {
  return {
    nombre: maquina.nombre,
    anio_fabricacion: textoDeNumero(maquina.anio_fabricacion),
    unidades: String(maquina.unidades),
    vida_util_total: textoDeNumero(maquina.vida_util_total),
    vida_util_residual: textoDeNumero(maquina.vida_util_residual),
    // Los decimales llegan como "48000.00": se muestran sin el relleno de
    // ceros y con coma, que es como se escriben acá. Con el punto crudo,
    // reabrir una máquina y guardarla sin tocar nada cambiaría su valor.
    valor_unitario_reposicion: textoDeDecimal(maquina.valor_unitario_reposicion),
    valor_unitario_residual: textoDeDecimal(maquina.valor_unitario_residual),
    ajuste_comercial_pct: textoDeDecimal(maquina.ajuste_comercial_pct),
    moneda: maquina.moneda ?? "USD",
  };
}

/**
 * El valor comercial que el servidor va a calcular con lo escrito, o `null`.
 *
 * ES EL GEMELO DE `InformeMaquinaria::valorComercialTotal()` y existe solo
 * para que el formulario muestre la cifra ANTES de guardar: unidades × valor
 * unitario residual (a dos decimales), por `1 + ajuste / 100` (a dos
 * decimales). El ajuste se aplica una vez sobre el total y NO se recorta un
 * resultado negativo, igual que allá. Cambiar la regla de un lado obliga a
 * cambiarla del otro en el mismo commit.
 *
 * `null` y nunca `0` sin valor residual: un valor sin cargar no es un equipo
 * que no vale nada. Las unidades vacías cuentan como 1, como en `camposDesde`.
 */
export function valorComercialDeLaMaquina(
  formulario: Pick<
    FormularioDeMaquina,
    "unidades" | "valor_unitario_residual" | "ajuste_comercial_pct"
  >,
): number | null {
  const residualUnitario = numeroEscrito(formulario.valor_unitario_residual);

  if (residualUnitario === null) return null;

  const unidades = numeroEscrito(formulario.unidades) ?? 1;
  const residual = aDosDecimales(residualUnitario * unidades);
  const ajuste = numeroEscrito(formulario.ajuste_comercial_pct) ?? 0;

  return aDosDecimales(residual * (1 + ajuste / 100));
}

/**
 * El cuerpo que viaja al servidor.
 *
 * Un campo vacío se manda como `null` y NO se omite: omitirlo en un PATCH
 * significa "no lo toques" (el request usa `sometimes`), así que vaciar un
 * valor no borraría nada. Son dos intenciones distintas y la pantalla ofrece
 * las dos.
 *
 * `unidades` es la excepción: vacío cae en 1. Es el único campo sin el cual no
 * se puede calcular ningún total, y el formulario ya arranca con ese valor —
 * mandar `null` sería un 422 sobre algo que la persona no eligió borrar.
 */
export function camposDesde(formulario: FormularioDeMaquina): CamposDeMaquinaria {
  return {
    nombre: formulario.nombre.trim(),
    anio_fabricacion: numeroEscrito(formulario.anio_fabricacion),
    unidades: numeroEscrito(formulario.unidades) ?? 1,
    vida_util_total: numeroEscrito(formulario.vida_util_total),
    vida_util_residual: numeroEscrito(formulario.vida_util_residual),
    valor_unitario_reposicion: numeroEscrito(formulario.valor_unitario_reposicion),
    valor_unitario_residual: numeroEscrito(formulario.valor_unitario_residual),
    ajuste_comercial_pct: numeroEscrito(formulario.ajuste_comercial_pct),
    moneda: formulario.moneda,
  };
}
